import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Save, Loader2, Trash2 } from 'lucide-react';
import FabricCanvas from '../../components/builder/FabricCanvas';
import ToolPanel from '../../components/builder/ToolPanel';
import AnimationPanel from '../../components/builder/AnimationPanel';

const API = '/api';
const CANVAS_W = 600;
const CANVAS_H = 200;

const ANIMATION_CSS = {
  none: '',
  bounce: '@keyframes _ez_bounce{0%{transform:scale(.8);opacity:0}60%{transform:scale(1.05)}100%{transform:scale(1);opacity:1}}.sig-wrap{animation:_ez_bounce .6s ease forwards}',
  fade: '@keyframes _ez_fade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}.sig-wrap{animation:_ez_fade .8s ease forwards}',
  slide: '@keyframes _ez_slide{from{transform:translateX(-30px);opacity:0}to{transform:translateX(0);opacity:1}}.sig-wrap{animation:_ez_slide .6s ease forwards}',
  pulse: '@keyframes _ez_pulse{0%,100%{box-shadow:0 0 5px rgba(59,130,246,.5)}50%{box-shadow:0 0 20px rgba(59,130,246,.9)}}.sig-wrap{animation:_ez_pulse 2s ease-in-out infinite}',
  glow: '@keyframes _ez_glow{0%,100%{filter:drop-shadow(0 0 4px rgba(139,92,246,.5))}50%{filter:drop-shadow(0 0 14px rgba(139,92,246,.9))}}.sig-wrap{animation:_ez_glow 2s ease-in-out infinite}',
  typewriter: '',
};

function generateHTML(dataUrl, animationType, w, h) {
  const css = ANIMATION_CSS[animationType] || '';
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
body{margin:0;padding:0;background:transparent}
${css}
</style>
</head>
<body>
<div class="sig-wrap" style="display:inline-block;width:${w}px;height:${h}px;">
<img src="${dataUrl}" width="${w}" height="${h}" style="display:block;" alt="Email Signature"/>
</div>
</body>
</html>`;
}

export default function SignatureBuilder() {
  const canvasRef = useRef(null);
  const [animation, setAnimation] = useState('none');
  const [sigName, setSigName] = useState('My Signature');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [mySignatures, setMySignatures] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedSig, setSelectedSig] = useState(null);

  useEffect(() => {
    axios.get(`${API}/email-signatures/`)
      .then(r => setMySignatures(r.data))
      .catch(() => {})
      .finally(() => setLoadingList(false));
  }, []);

  // Load template from session storage if set
  useEffect(() => {
    const templateJSON = sessionStorage.getItem('templateJSON');
    if (templateJSON) {
      sessionStorage.removeItem('templateJSON');
      try {
        const json = JSON.parse(templateJSON);
        setTimeout(() => {
          canvasRef.current?.loadJSON(json);
        }, 500);
      } catch {}
    }
  }, []);

  const exportHTML = () => {
    const dataUrl = canvasRef.current?.toDataURL();
    if (!dataUrl) return;
    const html = generateHTML(dataUrl, animation, CANVAS_W, CANVAS_H);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'email-signature.html';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportGIF = async () => {
    try {
      const GIF = (await import('gif.js')).default;
      const canvas = canvasRef.current?.getCanvas();
      if (!canvas) {
        alert('Canvas not ready');
        return;
      }
      const gif = new GIF({
        workers: 2,
        quality: 10,
        width: CANVAS_W,
        height: CANVAS_H,
        transparent: null,
      });
      for (let i = 0; i < 20; i++) {
        gif.addFrame(canvas.lowerCanvasEl, { delay: 100, copy: true });
      }
      gif.on('finished', (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'email-signature.gif';
        a.click();
        URL.revokeObjectURL(url);
      });
      gif.render();
    } catch (e) {
      alert('GIF export requires gif.js. Error: ' + e.message);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const json = canvasRef.current?.getJSON() || {};
      const dataUrl = canvasRef.current?.toDataURL();
      const html = dataUrl ? generateHTML(dataUrl, animation, CANVAS_W, CANVAS_H) : '';

      const payload = {
        name: sigName,
        canvas_json: json,
        html_export: html,
        animation_type: animation,
        canvas_width: CANVAS_W,
        canvas_height: CANVAS_H,
      };

      let res;
      if (selectedSig) {
        res = await axios.put(`${API}/email-signatures/${selectedSig}`, payload);
        setMySignatures(s => s.map(x => x.id === selectedSig ? res.data : x));
      } else {
        res = await axios.post(`${API}/email-signatures/`, payload);
        setMySignatures(s => [res.data, ...s]);
        setSelectedSig(res.data.id);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const loadSignature = (sig) => {
    canvasRef.current?.loadJSON(sig.canvas_json);
    setAnimation(sig.animation_type || 'none');
    setSigName(sig.name);
    setSelectedSig(sig.id);
  };

  const newSignature = () => {
    canvasRef.current?.clear();
    setSigName('My Signature');
    setAnimation('none');
    setSelectedSig(null);
  };

  const deleteSignature = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this signature?')) return;
    try {
      await axios.delete(`${API}/email-signatures/${id}`);
      setMySignatures(s => s.filter(x => x.id !== id));
      if (selectedSig === id) newSignature();
    } catch {
      alert('Delete failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Email Signature Builder</h1>
        <div className="flex items-center gap-3">
          <input
            value={sigName}
            onChange={e => setSigName(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-40"
            placeholder="Signature name"
          />
          <button
            onClick={newSignature}
            className="border border-gray-300 text-gray-600 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50"
          >
            New
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saved ? 'Saved!' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex gap-5">
        {/* Left panel */}
        <div className="w-52 flex-shrink-0 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <ToolPanel canvasRef={canvasRef} onExportHTML={exportHTML} onExportGIF={exportGIF} />
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <AnimationPanel value={animation} onChange={setAnimation} />
          </div>
        </div>

        {/* Canvas area */}
        <div className="flex-1 min-w-0">
          <div className="bg-gray-200 p-6 rounded-xl flex justify-center overflow-auto">
            <FabricCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} />
          </div>
          <p className="text-xs text-gray-400 text-center mt-2">
            {CANVAS_W}×{CANVAS_H}px · Standard email signature size
          </p>
        </div>

        {/* Right panel: saved signatures */}
        <div className="w-52 flex-shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Saved</h3>
              <Link to="/email-signatures/templates" className="text-xs text-blue-600 hover:underline">
                Templates
              </Link>
            </div>
            {loadingList ? (
              <p className="text-xs text-gray-400">Loading...</p>
            ) : mySignatures.length === 0 ? (
              <p className="text-xs text-gray-400 leading-relaxed">
                No saved signatures. Design one and click Save.
              </p>
            ) : (
              <div className="space-y-1.5">
                {mySignatures.map(s => (
                  <div
                    key={s.id}
                    onClick={() => loadSignature(s)}
                    className={`flex items-center justify-between p-2 border rounded-lg cursor-pointer text-xs transition-colors ${
                      selectedSig === s.id
                        ? 'border-blue-400 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <span className="truncate font-medium">{s.name}</span>
                    <button
                      onClick={(e) => deleteSignature(s.id, e)}
                      className="text-gray-400 hover:text-red-500 ml-1 flex-shrink-0"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
